import React from 'react';

import {
  AdoWorkItemType,
  GeneratedWorkItem,
} from '../../services/adoIntegrationService';

interface BacklogTreeViewProps {
  items: GeneratedWorkItem[];
  onChange: (next: GeneratedWorkItem[]) => void;
}

const TYPE_OPTIONS: AdoWorkItemType[] = [
  'Epic',
  'Feature',
  'User Story',
  'Product Backlog Item',
  'Requirement',
  'Issue',
  'Task',
  'Bug',
];

const TERTIARY_PILL =
  'bg-accent-tertiary-subtle text-accent-tertiary-strong border-accent-tertiary-medium/40';
const TERTIARY_ACCENT = 'border-l-accent-tertiary-strong';

/**
 * Visual treatment per ADO work-item type. Colors mirror the badges
 * Azure DevOps itself uses (purple Epic / red Feature / blue Story /
 * yellow Task / red-orange Bug) but adjusted to live inside Shell's
 * palette so the tree still reads as part of Archie.
 */
const TYPE_STYLES: Record<
  AdoWorkItemType,
  {
    icon: string;
    pill: string;
    accent: string;
    label: string;
  }
> = {
  Epic: {
    icon: '👑',
    pill: 'bg-purple-100 text-purple-800 border-purple-200',
    accent: 'border-l-purple-500',
    label: 'Epic',
  },
  Feature: {
    icon: '🏆',
    pill: 'bg-accent-secondary-subtle text-accent-secondary-strong border-accent-secondary-medium/40',
    accent: 'border-l-accent-secondary-strong',
    label: 'Feature',
  },
  'User Story': {
    icon: '📘',
    pill: TERTIARY_PILL,
    accent: TERTIARY_ACCENT,
    label: 'User Story',
  },
  'Product Backlog Item': {
    icon: '📘',
    pill: TERTIARY_PILL,
    accent: TERTIARY_ACCENT,
    label: 'Product Backlog Item',
  },
  Requirement: {
    icon: '📋',
    pill: TERTIARY_PILL,
    accent: TERTIARY_ACCENT,
    label: 'Requirement',
  },
  Issue: {
    icon: '📝',
    pill: TERTIARY_PILL,
    accent: TERTIARY_ACCENT,
    label: 'Issue',
  },
  Task: {
    icon: '✅',
    pill: 'bg-accent-primary-subtle text-text-onSurface-strong border-accent-primary-medium/40',
    accent: 'border-l-accent-primary-strong',
    label: 'Task',
  },
  Bug: {
    icon: '🐞',
    pill: 'bg-system-subtle-negative text-system-strong-negative border-system-strong-negative/30',
    accent: 'border-l-system-strong-negative',
    label: 'Bug',
  },
};

interface NodeProps {
  node: GeneratedWorkItem;
  depth: number;
  index: number;
  onChange: (next: GeneratedWorkItem) => void;
  onDelete: () => void;
}

/**
 * Recursive editor for a single backlog tree node. Edits are applied
 * via callbacks back to the parent so the whole tree stays controlled.
 */
const TreeNode: React.FC<NodeProps> = ({
  node,
  depth,
  index,
  onChange,
  onDelete,
}) => {
  const [expanded, setExpanded] = React.useState(true);
  const [showDetails, setShowDetails] = React.useState(false);
  const style = TYPE_STYLES[node.type as AdoWorkItemType] ?? TYPE_STYLES.Task;

  const updateChild = (idx: number, child: GeneratedWorkItem | null) => {
    const next = [...node.children];
    if (child === null) {
      next.splice(idx, 1);
    } else {
      next[idx] = child;
    }
    onChange({ ...node, children: next });
  };

  const hasChildren = node.children.length > 0;
  const hasDetails = Boolean(node.description || node.acceptance_criteria);

  return (
    <div className="relative">
      {/* eslint-disable-next-line max-len */}
      <div className={`group relative rounded-md border border-border-medium bg-background-surface hover:bg-background-section transition border-l-4 ${style.accent}`}>
        <div className="flex items-start gap-2 px-3 py-2">
          {/* Collapse / expand chevron */}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            disabled={!hasChildren}
            aria-label={expanded ? 'Collapse' : 'Expand'}
            className={`mt-0.5 h-5 w-5 flex items-center justify-center rounded text-text-onSurface-subtle ${
              hasChildren
                ? 'hover:bg-background-section cursor-pointer'
                : 'opacity-30 cursor-default'
            }`}
          >
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              className={`h-3 w-3 transition-transform ${
                expanded ? 'rotate-90' : ''
              }`}
            >
              <path d="M6 4l8 6-8 6V4z" />
            </svg>
          </button>

          {/* Type pill */}
          {/* eslint-disable-next-line max-len */}
          <span className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${style.pill}`} title={style.label}>
            <span aria-hidden>{style.icon}</span>
            {style.label}
          </span>

          {/* Title */}
          <div className="flex-1 min-w-0">
            <input
              className="w-full bg-transparent border-0 px-0 py-0.5 text-sm font-medium text-text-onSurface-strong focus:outline-none focus:ring-0 focus:bg-background-section rounded"
              value={node.title}
              onChange={(e) => onChange({ ...node, title: e.target.value })}
              placeholder="Untitled work item"
            />
            <div className="flex items-center gap-2 text-[11px] text-text-onSurface-subtle">
              <span>#{depth === 0 ? index + 1 : `${depth}.${index + 1}`}</span>
              {hasChildren && (
                <span>
                  · {node.children.length} child
                  {node.children.length === 1 ? '' : 'ren'}
                </span>
              )}
              {node.estimated_effort != null && (
                <span>· {node.estimated_effort} pts</span>
              )}
              {node.tags.length > 0 && (
                <span className="truncate">· {node.tags.join(', ')}</span>
              )}
            </div>
          </div>

          {/* Type selector */}
          <select
            className="select select-bordered select-xs"
            value={node.type}
            onChange={(e) =>
              onChange({ ...node, type: e.target.value as AdoWorkItemType })
            }
            aria-label="Work item type"
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>

          {/* Details toggle */}
          {hasDetails && (
            <button
              type="button"
              onClick={() => setShowDetails((v) => !v)}
              className="text-xs text-accent-tertiary-strong hover:underline"
            >
              {showDetails ? 'Hide' : 'Details'}
            </button>
          )}

          {/* Delete */}
          <button
            type="button"
            onClick={onDelete}
            aria-label="Remove"
            className="opacity-60 group-hover:opacity-100 text-system-strong-negative hover:bg-system-subtle-negative h-6 w-6 rounded flex items-center justify-center text-sm"
          >
            ✕
          </button>
        </div>

        {showDetails && hasDetails && (
          <div className="px-3 pb-2 -mt-1 border-t border-border-subtle">
            {node.description && (
              <div className="mt-2">
                <div className="text-[11px] uppercase tracking-wide text-text-onSurface-subtle font-semibold">
                  Description
                </div>
                <p className="text-xs text-text-onSurface-strong whitespace-pre-wrap mt-0.5">
                  {node.description}
                </p>
              </div>
            )}
            {node.acceptance_criteria && (
              <div className="mt-2">
                <div className="text-[11px] uppercase tracking-wide text-text-onSurface-subtle font-semibold">
                  Acceptance criteria
                </div>
                <p className="text-xs text-text-onSurface-strong whitespace-pre-wrap mt-0.5">
                  {node.acceptance_criteria}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {hasChildren && expanded && (
        <div className="relative mt-1.5 ml-3 pl-4 border-l-2 border-dashed border-border-medium space-y-1.5">
          {node.children.map((child, idx) => (
            <TreeNode
              key={child.temp_id}
              node={child}
              depth={depth + 1}
              index={idx}
              onChange={(updated) => updateChild(idx, updated)}
              onDelete={() => updateChild(idx, null)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface LegendChipProps {
  type: AdoWorkItemType;
}

const LegendChip: React.FC<LegendChipProps> = ({ type }) => {
  const style = TYPE_STYLES[type];
  return (
    // eslint-disable-next-line max-len
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${style.pill}`}>
      <span aria-hidden>{style.icon}</span>
      {style.label}
    </span>
  );
};

/**
 * Lightweight read/edit view of a generated backlog tree.
 *
 * Designed for "review then send" UX — supports editing titles + types
 * and removing nodes. Adding new nodes is intentionally out of scope
 * (the LLM round-trip is the source of truth).
 */
const BacklogTreeView: React.FC<BacklogTreeViewProps> = ({
  items,
  onChange,
}) => {
  const updateRoot = (idx: number, next: GeneratedWorkItem | null) => {
    const list = [...items];
    if (next === null) {
      list.splice(idx, 1);
    } else {
      list[idx] = next;
    }
    onChange(list);
  };

  if (items.length === 0) {
    return (
      <div className="rounded border border-dashed border-border-medium bg-background-section px-4 py-6 text-center text-sm text-text-onSurface-subtle">
        No items.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-text-onSurface-subtle">
        <span className="font-medium text-text-onSurface-strong">Legend:</span>
        {TYPE_OPTIONS.map((t) => (
          <LegendChip key={t} type={t} />
        ))}
      </div>
      <div className="rounded-lg border border-border-medium bg-background-base p-3 max-h-[560px] overflow-y-auto space-y-2">
        {items.map((root, idx) => (
          <TreeNode
            key={root.temp_id}
            node={root}
            depth={0}
            index={idx}
            onChange={(updated) => updateRoot(idx, updated)}
            onDelete={() => updateRoot(idx, null)}
          />
        ))}
      </div>
    </div>
  );
};

export default BacklogTreeView;
